import { useLayoutEffect, useRef, useState } from "react"

const VIEWPORT_MARGIN = 16
const GAP = 8
const MIN_MENU_HEIGHT = 80

/**
 * @param {boolean} isOpen
 * @param {{ maxHeightCap?: number | null }} [options]
 */
export function useAdminToolbarPopoutPosition(isOpen, { maxHeightCap = null } = {}) {
    const triggerRef = useRef(null)
    const menuRef = useRef(null)
    const [menuStyle, setMenuStyle] = useState({})

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuStyle({})
            return
        }

        function updatePosition() {
            const trigger = triggerRef.current
            const menu = menuRef.current
            if (!trigger || !menu) {
                return
            }

            const triggerRect = trigger.getBoundingClientRect()
            const menuRect = menu.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
            const maxMenuWidth = viewportWidth - VIEWPORT_MARGIN * 2

            const menuWidth = Math.min(
                Math.max(menuRect.width, menu.offsetWidth, 0),
                maxMenuWidth
            )

            // Anchor the menu bottom just above the trigger; it grows upward.
            const bottom = viewportHeight - triggerRect.top + GAP
            let maxHeight = Math.max(
                MIN_MENU_HEIGHT,
                triggerRect.top - GAP - VIEWPORT_MARGIN
            )
            if (typeof maxHeightCap === "number" && maxHeightCap > 0) {
                maxHeight = Math.min(maxHeight, maxHeightCap)
            }

            // Default: align menu left edge with trigger. If that won't fit, center on screen.
            const preferredLeft = triggerRect.left
            const fitsPreferredPosition =
                menuWidth <= 0 ||
                (preferredLeft >= VIEWPORT_MARGIN &&
                    preferredLeft + menuWidth <= viewportWidth - VIEWPORT_MARGIN)

            let left
            if (fitsPreferredPosition) {
                left = preferredLeft
            } else if (menuWidth > 0) {
                left = (viewportWidth - menuWidth) / 2
                left = Math.max(
                    VIEWPORT_MARGIN,
                    Math.min(left, viewportWidth - VIEWPORT_MARGIN - menuWidth)
                )
            } else {
                left = preferredLeft
            }

            setMenuStyle({
                position: "fixed",
                top: "auto",
                bottom: `${Math.round(bottom)}px`,
                left: `${Math.round(left)}px`,
                right: "auto",
                maxHeight: `${Math.round(maxHeight)}px`,
                maxWidth: `${Math.round(maxMenuWidth)}px`,
            })
        }

        updatePosition()
        const rafId = window.requestAnimationFrame(updatePosition)

        window.addEventListener("resize", updatePosition)

        function handleScroll(event) {
            if (menuRef.current?.contains(/** @type {Node} */ (event.target))) {
                return
            }
            updatePosition()
        }

        window.addEventListener("scroll", handleScroll, true)

        return () => {
            window.cancelAnimationFrame(rafId)
            window.removeEventListener("resize", updatePosition)
            window.removeEventListener("scroll", handleScroll, true)
        }
    }, [isOpen, maxHeightCap])

    return { triggerRef, menuRef, menuStyle }
}
